import React, { useEffect, useState, useRef } from 'react';
import { Button } from 'antd';
import _ from 'lodash';
import PropTypes from 'prop-types';

import Settings from './Settings';
import DragablePhotoAlbum from './DragablePhotoAlbum';
import utils from 'utils/utils';

/**
 * GalleryImageEditor
 * @param {Object} images - The images
 * @param {Object} config - The config
 * @param {Function} onLayoutChange - The function to handle the layout change
 * @param {Function} onConfigChange - The function to handle the config change
 * @param {number} containerWidth - The container width
 * @param {Object} setTemporary - The function to set the temporary
 */
const GalleryImageEditor = ({
  images,
  config,
  onLayoutChange,
  onConfigChange,
  containerWidth,
  setTemporary,
}) => {
  const lastOnSetPhotosCall = useRef(0);

  const [openSettings, setOpenSettings] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [isOldGallery, setIsOldGallery] = useState(false);

  useEffect(() => {
    const timeSinceLastOnSetPhotos = Date.now() - lastOnSetPhotosCall.current;
    if (timeSinceLastOnSetPhotos < 2000) {
      return;
    }

    const _isOldGallery = images?.length > 0 && images.some((img) => img.w === 5 && img.h === 4);
    setIsOldGallery(_isOldGallery);
    setPhotos(utils.albumImagesBuilder(images));
  }, [images]);

  /**
   * Handle the set photos
   * @param {Array} newPhotos - The new photos
   * @returns {void}
   */
  const onSetPhotos = (newPhotos) => {
    lastOnSetPhotosCall.current = Date.now();
    setPhotos(newPhotos);
  };

  /**
   * Debounce the layout change
   * @param {Array} value - The value to debounce
   * @returns {void}
   */
  const debounceLayoutChange = useRef(
    _.debounce((value) => {
      onLayoutChange(value);
    }, 500)
  ).current;

  /**
   * Render the gallery image editor
   * @returns {React.ReactNode} - The gallery image editor
   */
  return (
    <>
      <div className="gallery-image-editor" style={{ '--max-width': `${containerWidth}px` }}>
        <Settings
          {...{
            setOpenSettings,
            isOpen: openSettings,
            images,
            config,
            onConfigChange,
            onLayoutChange,
          }}>
          <DragablePhotoAlbum
            {...{
              isOldGallery,
              photos,
              setPhotos: onSetPhotos,
              onLayoutChange: debounceLayoutChange,
              setTemporary,
            }}
          />
        </Settings>
      </div>

      <Button
        className="open-setting-btn medium-btn grey-btn"
        onClick={() => setOpenSettings(!openSettings)}>
        Settings
      </Button>
    </>
  );
};

GalleryImageEditor.defaultProps = {
  images: [],
  config: {},
  onLayoutChange: () => {},
  onConfigChange: () => {},
  containerWidth: 0,
  setTemporary: () => {},
};

GalleryImageEditor.propTypes = {
  images: PropTypes.instanceOf(Array).isRequired,
  config: PropTypes.instanceOf(Object).isRequired,
  onLayoutChange: PropTypes.func.isRequired,
  onConfigChange: PropTypes.func.isRequired,
  containerWidth: PropTypes.number.isRequired,
  setTemporary: PropTypes.instanceOf(Object).isRequired,
};

export default GalleryImageEditor;
